import React, { useState } from "react";
import DropdownMenuDemo from "./PromotionRuleDropdown";
import { isObject } from "lodash";
import { cartProductOptions, getCartProductOptionLabel, validateTieredCartDiscountRows } from "./data";
import { useContext } from "react";

/** Saved payloads clone `options` at creation time; cart scope must always use current `cartProductOptions`. */
function optionsForCartScopeSelect(selectNode) {
  if (selectNode?.value === "on_cart_product") return cartProductOptions;
  return selectNode?.options;
}
import { PlatformContext } from "./AddPromotions";
import { Box, Button, Stack, Typography, IconButton } from "@mui/material";
import DeleteIcon from "common/customIcons/delete";

export default function ThenRuleView({
  data,
  thenRuleOpen,
  setThenRuleOpen,
  rulesStructure,
  setData,
  selectedRuleTypeRules,
  isThenMenuHappenOpen,
  setThenMenuHappenOpen,
  updateRulesRef,
  updateRuleValue,
  orderTotalExeedValue = 0,
}) {
  const platform = useContext(PlatformContext);
  const tieredRuleId = "tiered_cart_discount";
  const [tierFieldOpenMap, setTierFieldOpenMap] = useState({});

  const getTierRows = (rule) => (Array.isArray(rule?.selectedValue) ? rule.selectedValue : []);

  const updateTieredRule = (rule, updater) => {
    const currentRows = getTierRows(rule);
    const nextRows = updater(currentRows).map((row) => ({
      minCartTotal: row?.minCartTotal ?? "",
      discountPercent: row?.discountPercent ?? "",
      upto: row?.upto ?? "",
    }));
    const { isValid, rowErrors } = validateTieredCartDiscountRows(nextRows);

    const updatedThenRules = (updateRulesRef.current?.thenRules || data?.thenRules || []).map((item) =>
      item?.value === tieredRuleId
        ? {
          ...item,
          selectedValue: nextRows,
          tierErrors: rowErrors,
          isShowError: !isValid,
        }
        : item
    );

    updateRulesRef.current = {
      ...data,
      thenRules: updatedThenRules,
    };
    setData({
      ...data,
      showThenError: false,
      thenRules: updatedThenRules,
    });
  };

  const getTierHint = (rows = []) =>
    rows
      .map((tier) => ({
        min: Number(tier?.minCartTotal),
        discount: Number(tier?.discountPercent),
      }))
      .filter((tier) => !Number.isNaN(tier.min) && !Number.isNaN(tier.discount))
      .sort((a, b) => a.min - b.min)
      .map((tier) => `Spend $${tier.min} -> ${tier.discount}%`)
      .join(", ");

  const tierActionButtonSx = {
    color: "#fd4438",
    "&:hover": {
      color: "#6B7280",
    },
    "&.Mui-disabled": {
      color: "#D1D5DB",
    },
  };

  const tierFieldKey = (rowIndex, fieldName) => `tier_${rowIndex}_${fieldName}`;
  const isTierFieldOpen = (rowIndex, fieldName) =>
    Boolean(tierFieldOpenMap[tierFieldKey(rowIndex, fieldName)]);
  /** PromotionRuleDropdown calls setOpen(true|false); must NOT toggle — that desyncs the menu anchor. */
  const setTierFieldOpen = (rowIndex, fieldName, nextOpen) => {
    const key = tierFieldKey(rowIndex, fieldName);
    setTierFieldOpenMap((prev) => ({ ...prev, [key]: Boolean(nextOpen) }));
  };

  /** Replace entire "Then" clause from menu (tiered ⇄ percentage etc.). Shared by dropdowns. */
  const applyThenRuleSelection = ({ ruleValue }) => {
    const selectedRule = selectedRuleTypeRules.thenRules?.find((item) => item.value === ruleValue);
    if (!selectedRule) return;
    const nextRule =
      selectedRule?.value === tieredRuleId
        ? {
            ...selectedRule,
            selectedValue: [{ minCartTotal: "", discountPercent: "", upto: "" }],
            tierErrors: [{ minCartTotal: "Min cart total must be greater than 0" }],
            isShowError: true,
          }
        : selectedRule;

    setData((prevData) => {
      const nextState = {
        ...prevData,
        showThenError: false,
        thenRules: [nextRule],
      };
      updateRulesRef.current = {
        ...(updateRulesRef.current || prevData),
        ...nextState,
      };
      return nextState;
    });
    setThenRuleOpen(false);
    setTierFieldOpenMap({});
  };

  const removeTieredCartDiscountRule = () => {
    setData((prevData) => {
      const nextRules = (prevData?.thenRules || []).filter((r) => r.value !== tieredRuleId);
      const nextState = {
        ...prevData,
        showThenError: false,
        thenRules: nextRules,
      };
      updateRulesRef.current = {
        ...(updateRulesRef.current || prevData),
        ...nextState,
      };
      return nextState;
    });
    setTierFieldOpenMap({});
  };

  const getOnCartProductScopeLabel = (scopeNode) => {
    const sv = scopeNode?.selectedValue;
    const inner = scopeNode?.groupRule;
    const innerHasSelection =
      (Array.isArray(inner?.selectedValue) && inner.selectedValue.length > 0) ||
      (isObject(inner?.selectedValue) &&
        (inner.selectedValue.display_name ||
          inner.selectedValue.name ||
          inner.selectedValue.categoryName ||
          inner.selectedValue.brandName));

    if (typeof sv === "string") {
      if (sv === "category" && Array.isArray(inner?.selectedValue) && inner.selectedValue.length > 0) {
        return getCartProductOptionLabel("category") || "Select";
      }
      if (sv === "brand" && Array.isArray(inner?.selectedValue) && inner.selectedValue.length > 0) {
        return getCartProductOptionLabel("brand") || "Select";
      }
      return getCartProductOptionLabel(sv) || "Select";
    }

    // Legacy malformed state may store product payload here; keep product names only at inner picker.
    if (innerHasSelection) return getCartProductOptionLabel("products") || "Select";
    return "Select (cart, products...)";
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div className="fs-large text-gray-400 font-medium" style={{ display: "flex", flexWrap: "wrap" }}>
        {/* Render a dropdown if no 'thenRules' are present */}
        {data?.thenRules?.length === 0 && (
          <>
            <DropdownMenuDemo
              key="then-do-this"
              menuTrigger={
                <span className={thenRuleOpen ? "!text-blue-400 underline bg-red-400 fs-large" : ""}>
                  Then
                </span>
              }
              menuOptions={selectedRuleTypeRules.thenRules || []}
              isOpen={thenRuleOpen}
              setOpen={(open) => setThenRuleOpen(Boolean(open))}
              ruleType="then"
              rule="this"
              selectRuleHandler={applyThenRuleSelection}
              type="select"
              showError={data.showThenError}
              ruleId="then_rule"
              inputValue={data?.ruleType}
              disabled={!data?.ruleType}
            />
            <span>Apply</span>


          </>

        )}

        {/* Iterate over 'thenRules' to render each rule */}
        {data?.thenRules?.map((thenRule) => (
          <React.Fragment key={thenRule.value}>
            {thenRule?.value === tieredRuleId ? (
              <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" className="fs-large">
                  <DropdownMenuDemo
                    key="then-tiered-switch"
                    menuTrigger={
                      <span
                        className={`underline text-blue-400 ${data.showThenError ? "!text-red-400 !decoration-red-400" : ""}`}
                      >
                        Then
                      </span>
                    }
                    menuOptions={selectedRuleTypeRules.thenRules || []}
                    isOpen={thenRuleOpen}
                    setOpen={(open) => setThenRuleOpen(Boolean(open))}
                    ruleType="then"
                    rule="this"
                    selectRuleHandler={applyThenRuleSelection}
                    type="select"
                    showError={data.showThenError}
                    ruleId="then_rule"
                    inputValue={data?.ruleType}
                    disabled={!data?.ruleType}
                  />
                  <span>{thenRule.title || "Tiered cart discount"}</span>
                </Stack>
                {/* <Typography variant="body2" sx={{ color: "#6b7280" }}>
                  Customer gets the highest eligible tier. We will show upsell message for the next
                  tier.
                </Typography> */}

                {getTierRows(thenRule).map((tier, index) => {
                  const tierErrors = thenRule?.tierErrors?.[index] || {};
                  return (
                    <Stack
                      key={`tier-row-${index}`}
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ sm: "center" }}
                      className="fs-large text-gray-400 font-medium"
                    >
                      <span>Spend</span>
                      <DropdownMenuDemo
                        key={`tier-min-${index}`}
                        menuTrigger={
                          <span
                            className={`underline text-blue-400 ${tierErrors?.minCartTotal ? "!text-red-400 !decoration-red-400" : ""
                              }`}
                          >
                            ${tier?.minCartTotal || "min"}
                          </span>
                        }
                        menuOptions={selectedRuleTypeRules.thenRules || []}
                        isOpen={isTierFieldOpen(index, "minCartTotal")}
                        setOpen={(open) => setTierFieldOpen(index, "minCartTotal", open)}
                        ruleType="then"
                        rule="happen"
                        selectRuleHandler={({ ruleValue }) =>
                          updateTieredRule(thenRule, (rows) =>
                            rows.map((r, rIdx) =>
                              rIdx === index ? { ...r, minCartTotal: ruleValue } : r
                            )
                          )
                        }
                        type="input"
                        inputType="dollar"
                        ruleId={`tier_min_${index}`}
                        inputValue={tier?.minCartTotal ?? ""}
                        showError={Boolean(tierErrors?.minCartTotal)}
                      />
                      <span>to get</span>
                      <DropdownMenuDemo
                        key={`tier-discount-${index}`}
                        menuTrigger={
                          <span
                            className={`underline text-blue-400 ${tierErrors?.discountPercent ? "!text-red-400 !decoration-red-400" : ""
                              }`}
                          >
                            {tier?.discountPercent || "discount"}%
                          </span>
                        }
                        menuOptions={selectedRuleTypeRules.thenRules || []}
                        isOpen={isTierFieldOpen(index, "discountPercent")}
                        setOpen={(open) => setTierFieldOpen(index, "discountPercent", open)}
                        ruleType="then"
                        rule="happen"
                        selectRuleHandler={({ ruleValue }) =>
                          updateTieredRule(thenRule, (rows) =>
                            rows.map((r, rIdx) =>
                              rIdx === index ? { ...r, discountPercent: ruleValue } : r
                            )
                          )
                        }
                        type="input"
                        inputType="percentage"
                        ruleId={`tier_discount_${index}`}
                        inputValue={tier?.discountPercent ?? ""}
                        showError={Boolean(tierErrors?.discountPercent)}
                      />
                      <span>upto</span>
                      <DropdownMenuDemo
                        key={`tier-upto-${index}`}
                        menuTrigger={
                          <span
                            className={`underline text-blue-400 ${tierErrors?.upto ? "!text-red-400 !decoration-red-400" : ""
                              }`}
                          >
                            ${tier?.upto || "optional"}
                          </span>
                        }
                        menuOptions={selectedRuleTypeRules.thenRules || []}
                        isOpen={isTierFieldOpen(index, "upto")}
                        setOpen={(open) => setTierFieldOpen(index, "upto", open)}
                        ruleType="then"
                        rule="happen"
                        selectRuleHandler={({ ruleValue }) =>
                          updateTieredRule(thenRule, (rows) =>
                            rows.map((r, rIdx) => (rIdx === index ? { ...r, upto: ruleValue } : r))
                          )
                        }
                        type="input"
                        inputType="dollar"
                        ruleId={`tier_upto_${index}`}
                        inputValue={tier?.upto ?? ""}
                        showError={Boolean(tierErrors?.upto)}
                      />
                      <IconButton
                        type="button"
                        size="small"
                        variant="primary"
                        color="error"
                        style={{ marginLeft: "10px" }}
                        onClick={() => {
                          if (getTierRows(thenRule).length <= 1) {
                            removeTieredCartDiscountRule();
                          } else {
                            updateTieredRule(thenRule, (rows) =>
                              rows.filter((_, rIdx) => rIdx !== index)
                            );
                          }
                        }}
                        sx={tierActionButtonSx}
                      >
                        <DeleteIcon width="16px" height="16px" />
                      </IconButton>
                    </Stack>
                  );
                })}

                <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                  <Button
                    type="button"
                    size="small"
                    variant="text"
                    color="primary"
                    onClick={() =>
                      updateTieredRule(thenRule, (rows) => [
                        ...rows,
                        { minCartTotal: "", discountPercent: "", upto: "" },
                      ])
                    }
                    // sx={tierActionButtonSx}
                    style={{ background: "#0163FF", color: "#ffffff", lineHeight: "normal", padding: "7px 14px", fontSize: "12px", minHeight: "auto"}}
                  >
                    Add tier
                  </Button>
                  {thenRule?.tierErrors?.[0]?.general && (
                    <Typography variant="caption" sx={{ color: "#dc2626" }}>
                      {thenRule.tierErrors[0].general}
                    </Typography>
                  )}
                </Stack>

                {/* <Typography variant="body2" sx={{ color: "#6b7280" }}>
                  Next unlock examples: {getTierHint(getTierRows(thenRule)) || "-"}
                </Typography> */}
              </Box>
            ) : !(
              thenRule?.groupRule?.isGroupRule &&
              thenRule?.groupRule?.groupRule?.value === "buy_product" &&
              thenRule?.groupRule?.groupRule?.selectedValue === "any_product"
            ) && (
                <>
                  <DropdownMenuDemo
                    key="then-do-this"
                    menuTrigger={
                      <span
                        className={`underline text-blue-400 ${thenRuleOpen ? "!text-blue-400 underline bg-red-400" : ""
                          } ${data.showThenError ? "!text-red-400 !decoration-red-400" : ""}`}
                      >
                        Then
                      </span>
                    }
                    menuOptions={selectedRuleTypeRules.thenRules || []}
                    isOpen={thenRuleOpen}
                    setOpen={(open) => setThenRuleOpen(Boolean(open))}
                    ruleType="then"
                    rule="this"
                    selectRuleHandler={applyThenRuleSelection}
                    type="select"
                    showError={data.showThenError}
                    ruleId="then_rule"
                    inputValue={data?.ruleType}
                    disabled={!data?.ruleType}
                  />
                  {!(
                    thenRule &&
                    thenRule?.groupRule?.value === "get_one_product" &&
                    thenRule?.groupRule?.groupRule?.selectedValue === "any_product"
                  ) && <>{thenRule.title && <span>{thenRule.title}</span>}</>}

                  {/* group level one  */}
                  {thenRule.attributes === "each_or_any" && thenRule.selectedValue === "each" && (
                    <>
                      <DropdownMenuDemo
                        key={thenRule.groupRule.value}
                        menuTrigger={
                          <>
                            {thenRule?.groupRule?.inputType === "dollar" &&
                              thenRule?.groupRule.title &&
                              "$"}
                            {Array.isArray(thenRule?.groupRule?.selectedValue) ? (
                              thenRule.groupRule.selectedValue?.length > 0 ? (
                                <>
                                  {thenRule.groupRule.selectedValue
                                    ?.slice(0, 2)
                                    .map((value) =>
                                      thenRule.groupRule?.listType === "category"
                                        ? `${value?.categoryName}`
                                        : thenRule.groupRule?.listType === "brand"
                                          ? `${value?.brandName}`
                                          : `${value?.display_name}`
                                    )
                                    .join(", ") || thenRule.groupRule.highlightText}
                                  {thenRule.groupRule.selectedValue.length > 2 && (
                                    <span
                                      className="chip"
                                      data-tooltip-id="tooltip"
                                      data-tooltip-content={
                                        thenRule.groupRule.selectedValue
                                          .map((value) =>
                                            thenRule.groupRule?.listType === "category"
                                              ? `${value?.categoryName}`
                                              : thenRule.groupRule?.listType === "brand"
                                                ? `${value?.brandName}`
                                                : `${value?.display_name}`
                                          )
                                          .join(", ") || thenRule.groupRule.highlightText
                                      }
                                    >{`+${thenRule.groupRule.selectedValue.length - 2}`}</span>
                                  )}
                                </>
                              ) : (
                                thenRule.groupRule.highlightText
                              )
                            ) : isObject(thenRule?.groupRule?.selectedValue) ? (
                              thenRule?.groupRule?.listType === "category" ? (
                                thenRule?.groupRule?.selectedValue?.categoryName || "category"
                              ) : thenRule?.groupRule?.listType === "brand" ? (
                                thenRule?.groupRule?.selectedValue?.brandName || "brand"
                              ) : (
                                thenRule?.groupRule?.selectedValue?.display_name || "product"
                              )
                            ) : thenRule?.groupRule?.selectedValue === "any_product" ? (
                              "Then Get Any Product"
                            ) : (
                              (thenRule?.groupRule?.type === "select" &&
                              thenRule?.groupRule?.value === "on_cart_product"
                                ? getCartProductOptionLabel(thenRule.groupRule.selectedValue)
                                : thenRule?.groupRule?.type === "select" &&
                                    Array.isArray(thenRule?.groupRule?.options)
                                  ? thenRule.groupRule.options.find(
                                    (o) => o.value === thenRule.groupRule.selectedValue
                                  )?.title
                                  : null) ||
                              thenRule?.groupRule?.selectedValue ||
                              thenRule.groupRule.highlightText ||
                              "select"
                            )}
                            {thenRule?.groupRule?.inputType === "percentage" &&
                              thenRule?.groupRule.title &&
                              "%"}
                          </>
                        }
                        menuOptions={
                          thenRule.groupRule?.listType === "category"
                            ? [] // Categories not handled in ThenRuleView yet?
                            : thenRule.groupRule?.listType === "brand"
                              ? []
                              : thenRule.groupRule.type === "select"
                                ? optionsForCartScopeSelect(thenRule.groupRule)
                                : selectedRuleTypeRules.thenRules || []
                        }
                        isOpen={thenRule.groupRule.isOpen}
                        setOpen={() => {
                          let updatedThenRules = [...updateRulesRef.current.thenRules].map((rule) => {
                            if (rule.groupRule.value === thenRule.groupRule.value && rule.groupRule) {
                              return {
                                ...rule,
                                groupRule: {
                                  ...rule.groupRule,
                                  isOpen: !rule.groupRule.isOpen,

                                  // selectedValue: rule.groupRule.selectedValue
                                },
                              };
                            }
                            return rule;
                          });
                          updateRulesRef.current = {
                            ...data,
                            thenRules: [...updatedThenRules],
                          };
                          setData({
                            ...data,
                            thenRules: updatedThenRules,
                          });
                        }}
                        ruleType="then"
                        rule="happen"
                        selectRuleHandler={({ ruleValue }) => {
                          if (
                            thenRule.groupRule.type === "select" ||
                            thenRule.groupRule.type === "list"
                          ) {
                            const selectedRule = data.thenRules?.find(
                              (item) => item.groupRule.value === thenRule.groupRule.value
                            );
                            if (selectedRule) {
                              const index = data.thenRules.indexOf(selectedRule);
                              let existingRule = [...data.thenRules];
                              existingRule[index] = {
                                ...selectedRule,
                                isOpen: false,
                                isShowError: false,
                                groupRule: {
                                  ...selectedRule.groupRule,
                                  selectedValue: ruleValue,
                                  isShowError: false,
                                  // highlightText: ruleValue,
                                },
                              };
                              updateRulesRef.current = {
                                ...data,
                                thenRules: [...existingRule],
                              };
                              setData((prev) => {
                                return {
                                  ...prev,
                                  thenRules: [...existingRule],
                                };
                              });
                            }
                            return;
                          }
                          if (thenRule.groupRule.type === "input") {
                            const updatedRules = updateRuleValue({
                              rules: data.thenRules,
                              ruleValue,
                              ruleId: thenRule.groupRule.value,
                              isGroupRule: thenRule.isGroupRule,
                            });
                            setData({
                              ...data,
                              thenRules: updatedRules,
                            });
                            return;
                          }
                          let existingRule = [...data.thenRules];
                          const selectedRule = selectedRuleTypeRules.thenRules?.find(
                            (item) => item.value === ruleValue
                          );
                          if (selectedRule) {
                            existingRule = [...existingRule, selectedRule];
                          }
                          const thenRuleArray = data.thenRules.some((item) =>
                            selectedRuleTypeRules.thenRules.find(
                              (_item) => _item.value === item.value
                            )
                          );
                          if (thenRuleArray) {
                            let findRule = data.thenRules.find((item) =>
                              selectedRuleTypeRules.thenRules.find(
                                (_item) => _item.value === item.value
                              )
                            );
                            if (findRule) {
                              existingRule = existingRule.filter(
                                (item) => item.value !== findRule?.value
                              );
                            }
                          }
                          setData({
                            ...data,
                            thenRules: existingRule,
                          });
                        }}
                        rules={selectedRuleTypeRules.thenRules}
                        type={thenRule.groupRule?.type}
                        inputType={thenRule.groupRule?.inputType}
                        listType={thenRule.groupRule?.listType}
                        ruleId={thenRule.groupRule?.value}
                        inputValue={thenRule.groupRule.selectedValue}
                        isGroupRule={true}
                        showError={thenRule?.groupRule?.isShowError}
                      />
                    </>
                  )}
                  <DropdownMenuDemo
                    key={thenRule.value}
                    menuTrigger={
                      <span
                        className="underline"
                        style={{
                          color: thenRule.isShowError ? "red" : "text-blue-400",
                          textDecorationColor: thenRule.isShowError ? "red" : "#60a5fa",
                        }}
                      >
                        {thenRule.inputType === "dollar" && thenRule?.title && "$"}
                        {Array.isArray(thenRule?.selectedValue) ? (
                          thenRule.selectedValue?.length > 0 ? (
                            <>
                              {thenRule.selectedValue
                                ?.slice(0, 2)
                                .map((value) =>
                                  thenRule?.listType === "category"
                                    ? `${value?.categoryName}`
                                    : thenRule?.listType === "brand"
                                      ? `${value?.brandName}`
                                      : `${value?.display_name}`
                                )
                                .join(", ") ||
                                thenRule.highlightText ||
                                "Select"}
                              {thenRule.selectedValue.length > 2 && (
                                <span
                                  className="chip"
                                  data-tooltip-id="tooltip"
                                  data-tooltip-content={
                                    thenRule.selectedValue
                                      .map((value) =>
                                        thenRule?.listType === "category"
                                          ? `${value?.categoryName}`
                                          : thenRule?.listType === "brand"
                                            ? `${value?.brandName}`
                                            : `${value?.display_name}`
                                      )
                                      .join(", ") || thenRule.highlightText
                                  }
                                >{`+${thenRule.selectedValue.length - 2}`}</span>
                              )}
                            </>
                          ) : (
                            thenRule.highlightText
                          )
                        ) : isObject(thenRule?.selectedValue) ? (
                          thenRule?.listType === "category" ? (
                            thenRule?.selectedValue?.categoryName || "category"
                          ) : thenRule?.listType === "brand" ? (
                            thenRule?.selectedValue?.brandName || "brand"
                          ) : (
                            thenRule?.selectedValue?.display_name || "product"
                          )
                        ) : thenRule?.selectedValue === "any_product" ? (
                          "TGet Any Product"
                        ) : thenRule?.type !== "one-level" ? (
                          (thenRule?.type === "select" && Array.isArray(thenRule?.options)
                            ? thenRule.options.find((o) => o.value === thenRule.selectedValue)?.title
                            : null) ||
                          thenRule?.selectedValue ||
                          thenRule?.highlightText ||
                          "Select"
                        ) : (
                          ""
                        )}
                        {thenRule.inputType === "percentage" && thenRule?.title && "%"}
                      </span>
                    }
                    menuOptions={
                      thenRule?.listType === "brand"
                        ? []
                        : thenRule?.type === "select"
                          ? thenRule?.options
                          : selectedRuleTypeRules.thenRules || []
                    }
                    isOpen={isThenMenuHappenOpen}
                    setOpen={(open) => setThenMenuHappenOpen(Boolean(open))}
                    ruleType="then"
                    rule="happen"
                    selectRuleHandler={({ ruleValue }) => {
                      let existingRule = [...data.thenRules];
                      if (thenRule.type === "select" || thenRule.type === "list") {
                        const selectedRule = existingRule.find((item) => item.value === thenRule.value);
                        if (selectedRule) {
                          const index = existingRule.indexOf(selectedRule);
                          existingRule[index] = {
                            ...selectedRule,
                            selectedValue: ruleValue,
                            isOpen: false,
                            isShowError: false,
                          };
                          setData((prevData) => ({
                            ...prevData,
                            thenRules: existingRule,
                          }));
                        }
                        return;
                      }
                      if (thenRule.type === "input") {
                        const updatedRules = updateRuleValue({
                          rules: data.thenRules,
                          ruleValue,
                          ruleId: thenRule.value,
                          isGroupRule: false,
                        });
                        setData((prevData) => ({
                          ...prevData,
                          thenRules: updatedRules,
                        }));
                        return;
                      }
                      const selectedRule = selectedRuleTypeRules.thenRules?.find(
                        (item) => item.value === ruleValue
                      );
                      if (selectedRule) {
                        existingRule.push(selectedRule);
                      }
                      const thenRuleArray = data.thenRules.some((item) =>
                        selectedRuleTypeRules.thenRules.find(
                          (_item) => _item.value === item.value
                        )
                      );
                      if (thenRuleArray) {
                        existingRule = existingRule.filter((item) => item.value !== thenRule.value);
                      }
                      setData((prevData) => ({
                        ...prevData,
                        thenRules: existingRule,
                      }));
                    }}
                    type={thenRule?.type}
                    inputType={thenRule?.inputType}
                    listType={thenRule?.listType}
                    ruleId={thenRule?.value}
                    rules={selectedRuleTypeRules.thenRules}
                    isGroupRule={false}
                    inputValue={thenRule.selectedValue}
                    showError={thenRule.isShowError}
                    maxInputValue={orderTotalExeedValue}
                  />
                  {thenRule?.endTitle && <span>{thenRule?.endTitle}</span>}
                  {/* group level one  */}
                </>
              )}
            {/* group level two  */}
            {thenRule.isGroupRule && (
              <>
                {!(
                  thenRule &&
                  thenRule.groupRule.value === "get_one_product" &&
                  thenRule.groupRule.groupRule.selectedValue === "any_product"
                ) && (
                    <>
                      {thenRule.groupRule.title && (
                        <span>
                          {thenRule.value === "flash_sale_discount" && thenRule.endTitle ? "\u00A0" : ""}
                          {thenRule.groupRule.title}
                        </span>
                      )}

                      {thenRule.attributes === "each_or_any" &&
                        thenRule.selectedValue === "each" ? null : (
                        <>
                          <DropdownMenuDemo
                            key={thenRule.groupRule.value}
                            menuTrigger={
                              <>
                                {["products", "specific_product"].includes(thenRule.groupRule?.selectedValue) &&
                                  thenRule.groupRule?.value === "on_cart_product" ? (
                                  getCartProductOptionLabel(thenRule.groupRule?.selectedValue)
                                ) : (
                                  <>
                                    {thenRule?.groupRule?.inputType === "dollar" &&
                                      thenRule?.groupRule.title &&
                                      "$"}
                                    {Array.isArray(thenRule?.groupRule?.selectedValue) ? (
                                      thenRule.groupRule.selectedValue?.length > 0 ? (
                                        <>
                                          {thenRule.groupRule.selectedValue
                                            ?.slice(0, 2)
                                            .map((value) =>
                                              thenRule.groupRule?.listType === "category"
                                                ? `${value?.categoryName}`
                                                : thenRule.groupRule?.listType === "brand"
                                                  ? `${value?.brandName}`
                                                  : `${value?.display_name}`
                                            )
                                            .join(", ") || thenRule.groupRule.highlightText}
                                          {thenRule.groupRule.selectedValue.length > 2 && (
                                            <span
                                              className="chip"
                                              data-tooltip-id="tooltip"
                                              data-tooltip-content={
                                                thenRule.groupRule.selectedValue
                                                  .map((value) =>
                                                    thenRule.groupRule?.listType === "category"
                                                      ? `${value?.categoryName}`
                                                      : thenRule.groupRule?.listType === "brand"
                                                        ? `${value?.brandName}`
                                                        : `${value?.display_name}`
                                                  )
                                                  .join(", ") || thenRule.groupRule.highlightText
                                              }
                                            >{`+${thenRule.groupRule.selectedValue.length - 2}`}</span>
                                          )}
                                        </>
                                      ) : (
                                        thenRule.groupRule.highlightText
                                      )
                                    ) : isObject(thenRule?.groupRule?.selectedValue) ? (
                                      thenRule?.groupRule?.listType === "category" ? (
                                        thenRule?.groupRule?.selectedValue?.categoryName || "category"
                                      ) : thenRule?.groupRule?.listType === "brand" ? (
                                        thenRule?.groupRule?.selectedValue?.brandName || "brand"
                                      ) : (
                                        thenRule?.groupRule?.selectedValue?.display_name || "product"
                                      )
                                    ) : thenRule?.groupRule?.selectedValue === "any_product" ? (
                                      "Then Get Any Product"
                                    ) : (
                                      (thenRule?.groupRule?.type === "select" &&
                                      thenRule?.groupRule?.value === "on_cart_product"
                                        ? getCartProductOptionLabel(thenRule.groupRule.selectedValue)
                                        : thenRule?.groupRule?.type === "select" &&
                                            Array.isArray(thenRule?.groupRule?.options)
                                          ? thenRule.groupRule.options.find(
                                            (o) => o.value === thenRule.groupRule.selectedValue
                                          )?.title
                                          : null) ||
                                      thenRule?.groupRule?.selectedValue ||
                                      thenRule.groupRule.highlightText ||
                                      "select"
                                    )}
                                    {thenRule?.groupRule?.inputType === "percentage" &&
                                      thenRule?.groupRule.title &&
                                      "%"}
                                  </>
                                )}
                              </>
                            }
                            menuOptions={
                              thenRule.groupRule?.listType === "brand"
                                ? []
                                : thenRule.groupRule.type === "select"
                                  ? optionsForCartScopeSelect(thenRule.groupRule)
                                  : selectedRuleTypeRules.thenRules || []
                            }
                            isOpen={thenRule.groupRule.isOpen}
                            setOpen={() => {
                              let updatedThenRules = [...updateRulesRef.current.thenRules].map(
                                (rule) => {
                                  if (
                                    rule.groupRule.value === thenRule.groupRule.value &&
                                    rule.groupRule
                                  ) {
                                    return {
                                      ...rule,
                                      groupRule: {
                                        ...rule.groupRule,
                                        isOpen: !rule.groupRule.isOpen,
                                        // selectedValue: rule.groupRule.selectedValue
                                      },
                                    };
                                  }
                                  return rule;
                                }
                              );
                              updateRulesRef.current = {
                                ...data,
                                thenRules: [...updatedThenRules],
                              };
                              setData({
                                ...data,
                                thenRules: updatedThenRules,
                              });
                            }}
                            ruleType="then"
                            rule="happen"
                            selectRuleHandler={({ ruleValue }) => {
                              if (
                                thenRule.groupRule.type === "select" ||
                                thenRule.groupRule.type === "list"
                              ) {
                                const selectedRule = data.thenRules?.find(
                                  (item) => item.groupRule.value === thenRule.groupRule.value
                                );
                                if (selectedRule) {
                                  const needsList = ["products", "specific_product", "category", "brand"].includes(ruleValue);
                                  const listType =
                                    ruleValue === "category" ? "category" : ruleValue === "brand" ? "brand" : "product";
                                  const innerGroup = needsList
                                    ? selectedRule.groupRule?.groupRule
                                      ? {
                                          ...selectedRule.groupRule.groupRule,
                                          listType,
                                          selectedValue: [],
                                        }
                                      : {
                                          title: "",
                                          value: listType,
                                          highlightText: "Select",
                                          type: "list",
                                          listType,
                                          isOpen: false,
                                          selectedValue: [],
                                        }
                                    : undefined;
                                  const { groupRule: _dropNested, ...grBase } = selectedRule.groupRule;
                                  const nextGroupRule = {
                                    ...grBase,
                                    selectedValue: ruleValue,
                                    isShowError: false,
                                  };
                                  if (needsList && innerGroup != null) {
                                    nextGroupRule.groupRule = innerGroup;
                                  }
                                  const index = data.thenRules.indexOf(selectedRule);
                                  let existingRule = [...data.thenRules];
                                  existingRule[index] = {
                                    ...selectedRule,
                                    isOpen: false,
                                    isShowError: false,
                                    groupRule: nextGroupRule,
                                  };
                                  updateRulesRef.current = {
                                    ...data,
                                    thenRules: [...existingRule],
                                  };
                                  setData((prev) => {
                                    return {
                                      ...prev,
                                      thenRules: [...existingRule],
                                    };
                                  });
                                }
                                return;
                              }
                              if (thenRule.groupRule.type === "input") {
                                const updatedRules = updateRuleValue({
                                  rules: data.thenRules,
                                  ruleValue,
                                  ruleId: thenRule.groupRule.value,
                                  isGroupRule: thenRule.isGroupRule,
                                });
                                setData({
                                  ...data,
                                  thenRules: updatedRules,
                                });
                                return;
                              }
                              let existingRule = [...data.thenRules];
                              const selectedRule = rulesStructure[data.ruleType].thenRules?.find(
                                (item) => item.value === ruleValue
                              );
                              if (selectedRule) {
                                existingRule = [...existingRule, selectedRule];
                              }
                              const thenRuleArray = data.thenRules.some((item) =>
                                rulesStructure[data.ruleType].thenRules.find(
                                  (_item) => _item.value === item.value
                                )
                              );
                              if (thenRuleArray) {
                                let findRule = data.thenRules.find((item) =>
                                  rulesStructure[data.ruleType].thenRules.find(
                                    (_item) => _item.value === item.value
                                  )
                                );
                                if (findRule) {
                                  existingRule = existingRule.filter(
                                    (item) => item.value !== findRule?.value
                                  );
                                }
                              }
                              setData({
                                ...data,
                                thenRules: existingRule,
                              });
                            }}
                            rules={selectedRuleTypeRules.thenRules}
                            type={thenRule.groupRule?.type}
                            inputType={thenRule.groupRule?.inputType}
                            listType={thenRule.groupRule?.listType}
                            ruleId={thenRule.groupRule?.value}
                            inputValue={thenRule.groupRule.selectedValue}
                            isGroupRule={true}
                            showError={thenRule?.groupRule?.isShowError}
                          />
                        </>
                      )}
                    </>
                  )}

                {!["each_or_any"].includes(thenRule?.attributes) && thenRule?.groupRule?.endTitle && (
                  <span>{thenRule?.groupRule?.endTitle}</span>
                )}

                {((thenRule?.groupRule?.groupRule?.isGroupRule || false) ||
                  thenRule.groupRule?.value === "mystery_discount_max_percent") &&
                  thenRule.groupRule?.groupRule.title && (
                    <span>{thenRule.groupRule?.groupRule.title}</span>
                  )}

                {/* group level two  */}

                {/* nested group rule  */}

                {thenRule.groupRule?.groupRule &&
                  thenRule.groupRule &&
                  (thenRule.attributes === "each_or_any" ||
                    thenRule.attributes === "upto_discount" ||
                    thenRule.groupRule.attributes === "upto_discount" ||
                    thenRule.groupRule.attributes === "more_to_get" ||
                    thenRule.attributes === "bogo_get_quantity" ||
                    thenRule.groupRule?.value === "mystery_discount_max_percent" ||
                    ["products", "specific_product", "category", "brand"].includes(thenRule.groupRule?.selectedValue) ||
                    thenRule.groupRule?.value === "flash_sale_start_time" ||
                    thenRule.groupRule?.groupRule?.value === "flash_sale_end_time") && (
                    <>
                      <DropdownMenuDemo
                        key={thenRule.groupRule?.groupRule.value}
                        menuTrigger={
                          thenRule.groupRule?.groupRule?.value === "on_cart_product" ? (
                            getOnCartProductScopeLabel(thenRule.groupRule?.groupRule)
                          ) : (
                            <>
                              {thenRule?.groupRule?.groupRule?.inputType === "dollar" &&
                                thenRule?.groupRule?.groupRule?.title &&
                                "$"}
                              {Array.isArray(thenRule?.groupRule?.groupRule?.selectedValue) ? (
                                thenRule.groupRule?.groupRule.selectedValue?.length > 0 ? (
                                  <>
                                    {thenRule.groupRule?.groupRule.selectedValue
                                      ?.slice(0, 2)
                                      .map((value) =>
                                        thenRule.groupRule?.groupRule?.listType === "category"
                                          ? `${value?.categoryName}`
                                          : thenRule.groupRule?.groupRule?.listType === "brand"
                                            ? `${value?.brandName}`
                                            : `${value?.display_name}`
                                      )
                                      .join(", ") ||
                                      thenRule.groupRule?.groupRule.highlightText ||
                                      "Select"}
                                    {thenRule.groupRule?.groupRule.selectedValue.length > 2 && (
                                      <span
                                        className="chip"
                                        data-tooltip-id="tooltip"
                                        data-tooltip-content={
                                          thenRule.groupRule?.groupRule.selectedValue
                                            .map((value) =>
                                              thenRule.groupRule?.groupRule?.listType === "category"
                                                ? `${value?.categoryName}`
                                                : thenRule.groupRule?.groupRule?.listType === "brand"
                                                  ? `${value?.brandName}`
                                                  : `${value?.display_name}`
                                            )
                                            .join(", ") ||
                                          thenRule.groupRule?.groupRule.highlightText ||
                                          "Select"
                                        }
                                      >{`+${thenRule.groupRule?.groupRule.selectedValue.length - 2
                                        }`}</span>
                                    )}
                                  </>
                                ) : (
                                  thenRule.groupRule?.groupRule.highlightText || "Select"
                                )
                              ) : isObject(thenRule?.groupRule?.groupRule?.selectedValue) ? (
                                thenRule?.groupRule?.groupRule?.listType === "category" ? (
                                  thenRule?.groupRule?.groupRule?.selectedValue?.categoryName ||
                                  "category"
                                ) : thenRule?.groupRule?.groupRule?.listType === "brand" ? (
                                  thenRule?.groupRule?.groupRule?.selectedValue?.brandName || "brand"
                                ) : (
                                  thenRule?.groupRule?.groupRule?.selectedValue?.display_name ||
                                  "product"
                                )
                              ) : thenRule?.groupRule?.groupRule?.selectedValue === "any_product" ? (
                                "Then Get Any Product"
                              ) : (
                                (thenRule?.groupRule?.groupRule?.type === "select" &&
                                  Array.isArray(thenRule?.groupRule?.groupRule?.options)
                                  ? thenRule.groupRule.groupRule.options.find(
                                    (o) =>
                                      o.value === thenRule.groupRule.groupRule.selectedValue
                                  )?.title
                                  : null) ||
                                thenRule?.groupRule?.groupRule?.selectedValue ||
                                thenRule.groupRule?.groupRule?.highlightText ||
                                "select"
                              )}
                              {thenRule?.groupRule?.groupRule?.inputType === "percentage" &&
                                thenRule?.groupRule?.groupRule?.title &&
                                "%"}
                            </>
                          )
                        }
                        menuOptions={
                          thenRule.groupRule?.groupRule?.listType === "brand"
                            ? []
                            : thenRule.groupRule?.groupRule?.type === "select"
                              ? optionsForCartScopeSelect(thenRule.groupRule.groupRule)
                              : selectedRuleTypeRules?.thenRules || []
                        }
                        isOpen={thenRule.groupRule?.groupRule?.isOpen}
                        setOpen={() => {
                          let updatedThenRules = [...(updateRulesRef?.current?.thenRules || {})].map(
                            (rule) => {
                              if (
                                rule.groupRule?.groupRule.value ===
                                thenRule.groupRule?.groupRule.value &&
                                rule.groupRule?.groupRule
                              ) {
                                return {
                                  ...rule,
                                  groupRule: {
                                    ...rule.groupRule,
                                    groupRule: {
                                      ...rule.groupRule?.groupRule,
                                      isOpen: !rule.groupRule?.groupRule.isOpen,
                                    },
                                  },
                                };
                              }
                              return rule;
                            }
                          );
                          updateRulesRef.current = {
                            ...data,
                            thenRules: [...updatedThenRules],
                          };
                          setData({
                            ...data,
                            thenRules: updatedThenRules,
                          });
                        }}
                        ruleType="then"
                        rule="happen"
                        selectRuleHandler={({ ruleValue }) => {
                          if (
                            thenRule.groupRule?.groupRule?.type === "select" ||
                            thenRule.groupRule?.groupRule?.type === "list"
                          ) {
                            const selectedRule = [...updateRulesRef.current.thenRules]?.find(
                              (item) =>
                                item.groupRule?.groupRule?.value ===
                                thenRule.groupRule?.groupRule?.value
                            );
                            if (selectedRule) {
                              const index = data.thenRules.indexOf(selectedRule);
                              const needsList = ["products", "specific_product", "category", "brand"].includes(ruleValue);
                              const listType = ruleValue === "category" ? "category" : ruleValue === "brand" ? "brand" : "product";
                              const innerGroup = needsList
                                ? selectedRule.groupRule?.groupRule?.groupRule
                                  ? {
                                      ...selectedRule.groupRule.groupRule.groupRule,
                                      listType,
                                      selectedValue: [],
                                    }
                                  : {
                                      title: "",
                                      value: listType,
                                      highlightText: "Select",
                                      type: "list",
                                      listType,
                                      isOpen: false,
                                      selectedValue: [],
                                    }
                                : undefined;
                              const { groupRule: _dropNestedInner, ...middleBase } =
                                selectedRule.groupRule?.groupRule || {};
                              const mergedMiddle = {
                                ...middleBase,
                                selectedValue: ruleValue,
                                isShowError: false,
                              };
                              if (needsList && innerGroup != null) {
                                mergedMiddle.groupRule = innerGroup;
                              }
                              let existingRule = [...data.thenRules];
                              existingRule[index] = {
                                ...selectedRule,
                                isOpen: false,
                                isShowError: false,
                                groupRule: {
                                  ...selectedRule.groupRule,
                                  groupRule: mergedMiddle,
                                },
                              };
                              updateRulesRef.current = {
                                ...data,
                                thenRules: [...existingRule],
                              };
                              setData({
                                ...data,
                                thenRules: existingRule,
                              });
                            }
                            return;
                          }
                          if (thenRule.groupRule?.groupRule?.type === "input") {
                            const updatedRules = [...updateRulesRef.current.thenRules].map((rule) => {
                              if (rule.groupRule?.groupRule?.value === thenRule.groupRule?.groupRule?.value) {
                                return {
                                  ...rule,
                                  groupRule: {
                                    ...rule.groupRule,
                                    groupRule: {
                                      ...rule.groupRule.groupRule,
                                      selectedValue: ruleValue,
                                      isShowError: false,
                                    },
                                  },
                                };
                              }
                              return rule;
                            });
                            updateRulesRef.current = { ...data, thenRules: updatedRules };
                            setData({ ...data, thenRules: updatedRules });
                            return;
                          }
                          if (thenRule.type === "input") {
                            const updatedRules = updateRuleValue({
                              rules: updateRulesRef.current.thenRules,
                              ruleValue,
                              ruleId: thenRule.value,
                              isGroupRule: thenRule.isGroupRule,
                            });
                            setData({
                              ...data,
                              thenRules: updatedRules,
                            });
                            return;
                          }
                          let existingRule = [...data.thenRules];
                          const selectedRule = selectedRuleTypeRules?.thenRules?.find(
                            (item) => item.value === ruleValue
                          );
                          if (selectedRule) {
                            existingRule = [...existingRule, selectedRule];
                          }
                          const thenRuleArray = data.thenRules.some((item) =>
                            selectedRuleTypeRules?.thenRules?.find(
                              (_item) => _item.value === item.value
                            )
                          );
                          if (thenRuleArray) {
                            let findRule = data.thenRules.find((item) =>
                              selectedRuleTypeRules?.thenRules?.find(
                                (_item) => _item.value === item.value
                              )
                            );
                            if (findRule) {
                              existingRule = existingRule.filter(
                                (item) => item.value !== findRule?.value
                              );
                            }
                          }
                          setData({
                            ...data,
                            thenRules: existingRule,
                          });
                        }}
                        rules={selectedRuleTypeRules.thenRules}
                        type={thenRule.groupRule?.groupRule?.type}
                        inputType={thenRule.groupRule?.groupRule?.inputType}
                        listType={thenRule.groupRule?.groupRule?.listType}
                        ruleId={thenRule.groupRule?.groupRule?.value}
                        inputValue={thenRule.groupRule?.groupRule.selectedValue}
                        isGroupRule={true}
                        showError={thenRule?.groupRule?.groupRule?.isShowError}
                      />
                      {<span>{thenRule?.groupRule?.groupRule?.endTitle}</span>}

                      {/* group level three  */}

                      {thenRule?.groupRule?.groupRule &&
                        thenRule.groupRule?.groupRule?.groupRule &&
                        ["products", "specific_product", "category", "brand"].includes(thenRule.groupRule.groupRule?.selectedValue) && (
                          <>
                            {thenRule.groupRule?.groupRule?.groupRule &&
                              thenRule.groupRule?.groupRule?.groupRule.title && (
                                <span>{thenRule.groupRule?.groupRule?.groupRule.title}</span>
                              )}
                            <DropdownMenuDemo
                              key={thenRule.groupRule?.groupRule?.groupRule.value}
                              menuTrigger={
                                <>
                                  {thenRule?.groupRule?.groupRule?.groupRule?.inputType ===
                                    "dollar" &&
                                    thenRule?.groupRule?.groupRule?.groupRule?.title &&
                                    "$"}
                                  {Array.isArray(
                                    thenRule?.groupRule?.groupRule?.groupRule?.selectedValue
                                  ) ? (
                                    thenRule.groupRule?.groupRule?.groupRule.selectedValue?.length >
                                      0 ? (
                                      <>
                                        {thenRule.groupRule?.groupRule?.groupRule.selectedValue
                                          ?.slice(0, 2)
                                          .map((value) =>
                                            thenRule.groupRule?.groupRule?.groupRule?.listType ===
                                              "category"
                                              ? `${value?.categoryName}`
                                              : thenRule.groupRule?.groupRule?.groupRule?.listType ===
                                                "brand"
                                                ? `${value?.brandName}`
                                                : `${value?.display_name}`
                                          )
                                          .join(", ") ||
                                          thenRule.groupRule?.groupRule?.groupRule.highlightText ||
                                          "Select"}
                                        {thenRule.groupRule?.groupRule?.groupRule.selectedValue
                                          .length > 2 && (
                                            <span
                                              className="chip"
                                              data-tooltip-id="tooltip"
                                              data-tooltip-content={
                                                thenRule.groupRule?.groupRule?.groupRule.selectedValue
                                                  .map((value) =>
                                                    thenRule.groupRule?.groupRule?.groupRule
                                                      ?.listType === "category"
                                                      ? `${value?.categoryName}`
                                                      : thenRule.groupRule?.groupRule?.groupRule
                                                        ?.listType === "brand"
                                                        ? `${value?.brandName}`
                                                        : `${value?.display_name}`
                                                  )
                                                  .join(", ") ||
                                                thenRule.groupRule?.groupRule?.groupRule
                                                  .highlightText ||
                                                "Select"
                                              }
                                            >{`+${thenRule.groupRule?.groupRule?.groupRule.selectedValue
                                              .length - 2
                                              }`}</span>
                                          )}
                                      </>
                                    ) : (
                                      thenRule.groupRule?.groupRule?.groupRule.highlightText ||
                                      "Select"
                                    )
                                  ) : isObject(
                                    thenRule?.groupRule?.groupRule?.groupRule?.selectedValue
                                  ) ? (
                                    thenRule?.groupRule?.groupRule?.groupRule?.listType ===
                                      "category" ? (
                                      thenRule?.groupRule?.groupRule?.groupRule?.selectedValue
                                        ?.categoryName || "category"
                                    ) : thenRule?.groupRule?.groupRule?.groupRule?.listType ===
                                      "brand" ? (
                                      thenRule?.groupRule?.groupRule?.groupRule?.selectedValue
                                        ?.brandName || "brand"
                                    ) : (
                                      thenRule?.groupRule?.groupRule?.groupRule?.selectedValue
                                        ?.display_name || "product"
                                    )
                                  ) : thenRule?.groupRule?.groupRule?.groupRule?.selectedValue ===
                                    "any_product" ? (
                                    "Then Get Any Product"
                                  ) : (
                                    thenRule?.groupRule?.groupRule?.groupRule?.selectedValue ||
                                    thenRule.groupRule?.groupRule?.groupRule?.highlightText ||
                                    "select"
                                  )}
                                  {thenRule?.groupRule?.groupRule?.groupRule?.inputType ===
                                    "percentage" &&
                                    thenRule?.groupRule?.groupRule?.groupRule?.title &&
                                    "%"}
                                </>
                              }
                              menuOptions={
                                thenRule.groupRule?.groupRule?.groupRule?.listType === "brand"
                                  ? []
                                  : thenRule.groupRule?.groupRule?.groupRule.type === "select"
                                    ? thenRule.groupRule?.groupRule?.groupRule.options
                                    : selectedRuleTypeRules.thenRules || []
                              }
                              isOpen={thenRule.groupRule?.groupRule?.groupRule.isOpen}
                              setOpen={() => {
                                let updatedThenRules = [...updateRulesRef.current.thenRules].map(
                                  (rule) => {
                                    if (
                                      rule.groupRule?.groupRule?.groupRule.value ===
                                      thenRule.groupRule?.groupRule?.groupRule?.value &&
                                      rule.groupRule?.groupRule?.groupRule
                                    ) {
                                      return {
                                        ...rule,
                                        groupRule: {
                                          ...rule.groupRule,
                                          groupRule: {
                                            ...rule.groupRule.groupRule,
                                            groupRule: {
                                              ...rule.groupRule.groupRule.groupRule,
                                              isOpen: !rule.groupRule.groupRule.groupRule.isOpen,
                                            },
                                          },
                                        },
                                      };
                                    }
                                    return rule;
                                  }
                                );
                                updateRulesRef.current = {
                                  ...data,
                                  thenRules: [...updatedThenRules],
                                };
                                setData({
                                  ...data,
                                  thenRules: updatedThenRules,
                                });
                              }}
                              ruleType="then"
                              rule="happen"
                              selectRuleHandler={({ ruleValue }) => {
                                if (
                                  thenRule.groupRule?.groupRule?.groupRule?.type === "select" ||
                                  thenRule.groupRule?.groupRule?.groupRule?.type === "list"
                                ) {
                                  const selectedRule = [...updateRulesRef.current.thenRules]?.find(
                                    (item) =>
                                      item.groupRule?.groupRule?.groupRule?.value ===
                                      thenRule.groupRule?.groupRule?.groupRule?.value
                                  );
                                  if (selectedRule) {
                                    const index = data.thenRules.indexOf(selectedRule);
                                    const currentScope = selectedRule.groupRule?.groupRule?.selectedValue;
                                    const normalizedScope =
                                      typeof currentScope === "string" ? currentScope : "products";
                                    let existingRule = [...data.thenRules];
                                    existingRule[index] = {
                                      ...selectedRule,
                                      isOpen: false,
                                      isShowError: false,
                                      groupRule: {
                                        ...selectedRule.groupRule,
                                        groupRule: {
                                          ...selectedRule.groupRule.groupRule,
                                          selectedValue: normalizedScope,
                                          groupRule: {
                                            ...selectedRule.groupRule.groupRule.groupRule,
                                            // isOpen: !selectedRule.groupRule.groupRule.groupRule.isOpen,
                                            selectedValue: ruleValue,
                                          },
                                        },
                                      },
                                    };
                                    updateRulesRef.current = {
                                      ...data,
                                      thenRules: [...existingRule],
                                    };
                                    setData({
                                      ...data,
                                      thenRules: existingRule,
                                    });
                                  }
                                  return;
                                }
                                if (thenRule.type === "input") {
                                  const updatedRules = updateRuleValue({
                                    rules: updateRulesRef.current.thenRules,
                                    ruleValue,
                                    ruleId: thenRule.value,
                                    isGroupRule: thenRule.isGroupRule,
                                  });
                                  setData({
                                    ...data,
                                    thenRules: updatedRules,
                                  });
                                  return;
                                }
                                let existingRule = [...data.thenRules];
                                const selectedRule = selectedRuleTypeRules?.thenRules?.find(
                                  (item) => item.value === ruleValue
                                );
                                if (selectedRule) {
                                  existingRule = [...existingRule, selectedRule];
                                }
                                const thenRuleArray = data.thenRules.some((item) =>
                                  selectedRuleTypeRules?.thenRules?.find(
                                    (_item) => _item.value === item.value
                                  )
                                );
                                if (thenRuleArray) {
                                  let findRule = data.thenRules.find((item) =>
                                    selectedRuleTypeRules?.thenRules?.find(
                                      (_item) => _item.value === item.value
                                    )
                                  );
                                  if (findRule) {
                                    existingRule = existingRule.filter(
                                      (item) => item.value !== findRule?.value
                                    );
                                  }
                                }
                                setData({
                                  ...data,
                                  thenRules: existingRule,
                                });
                              }}
                              rules={selectedRuleTypeRules.thenRules}
                              type={thenRule.groupRule?.groupRule?.groupRule?.type}
                              inputType={thenRule.groupRule?.groupRule?.groupRule?.inputType}
                              listType={thenRule.groupRule?.groupRule?.groupRule?.listType}
                              ruleId={thenRule.groupRule?.groupRule?.groupRule?.value}
                              inputValue={thenRule.groupRule?.groupRule?.groupRule?.selectedValue}
                              isGroupRule={true}
                              showError={thenRule?.groupRule?.groupRule?.groupRule?.isShowError}
                            />
                            {<span>{thenRule.groupRule?.groupRule?.groupRule?.endTitle}</span>}
                          </>
                        )}

                      {/* group level three  */}
                    </>
                  )}
              </>
            )}
          </React.Fragment>
        ))}
      </div>

    </div>
  );
}
